import React, {useCallback, useEffect, useRef} from "react";
import {Linking, Platform, useWindowDimensions, View, ScrollView} from "react-native";
import MainStyle from "../../../MainStyle";
import SupportGuideHeader from "../../components/SupportGuideHeader";
import {colors} from "../../assets/styles/colors";
import {useNavigation, useRoute} from "@react-navigation/native";
import {breakpoint, isFullSwing} from "../../constants";
import SupportGuideButtonMenu from "../../components/SupportGuideButtonMenu";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import FSHeader from "../../components/FSHeader";
import {PromotionsTextProvider, ScrollTarget, ScrollTargetOptions} from "./PromotionsText";
import IntroductionSection from "./sections/IntroductionSection";
import PromotionPeriodsSection from "./sections/PromotionPeriodsSection";
import PromotionCommunicationsSection from "./sections/PromotionCommunicationsSection";
import EligibilitySection from "./sections/EligibilitySection";
import ParticipationSection from "./sections/ParticipationSection";
import ExcludedPlayersSection from "./sections/ExcludedPlayersSection";
import QualifyingDepositsSection from "./sections/QualifyingDepositsSection";
import PaymentBonusesSection from "./sections/PaymentBonusesSection";
import PrizesSection from "./sections/PrizesSection";
import PrizeDrawsSection from "./sections/PrizeDrawsSection";
import PublicitySection from "./sections/PublicitySection";
import PlayRestrictionsSection from "./sections/PlayRestrictionsSection";
import CurrencySection from "./sections/CurrencySection";
import LiabilitySection from "./sections/LiabilitySection";
import AlterationSection from "./sections/AlterationSection";
import GeneralSection from "./sections/GeneralSection";
import HoleInOneSection from "./sections/HoleInOneSection";

type Props = { onExit?: () => void };

const scrollIntoViewOptions: ScrollTargetOptions = {behavior: 'smooth', block: 'start', inline: 'nearest'};

const isScrollTarget = (node: unknown): node is ScrollTarget => (
  typeof (node as { scrollIntoView?: unknown } | null)?.scrollIntoView === 'function'
);

const PromotionsScreen = ({onExit: onExitOverride}: Props = {}) => {
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const route: any = useRoute();
  const {width} = useWindowDimensions();
  const isSm = width < breakpoint;
  const eligibilitySectionRef = useRef<ScrollTarget | null>(null);
  const promotionsAndPromotionPeriodRef = useRef<ScrollTarget | null>(null);

  const openLink = (url: string) => {
    if (Platform.OS === 'web') window.open(url, '_blank', 'noopener,noreferrer');
    else Linking.openURL(url).then();
  };

  const onExit = useCallback(() => {
    if (onExitOverride) {
      onExitOverride();
      return;
    }

    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('challenges');
  }, [navigation, onExitOverride]);

  const setEligibilitySectionRef = useCallback((node: unknown) => {
    eligibilitySectionRef.current = isScrollTarget(node) ? node : null;
  }, []);

  const setPromotionsAndPromotionPeriodRef = useCallback((node: unknown) => {
    promotionsAndPromotionPeriodRef.current = isScrollTarget(node) ? node : null;
  }, []);

  useEffect(() => {
    if (route.params?.scrollTo === 'ELIGIBILITY_SECTION') {
      eligibilitySectionRef.current?.scrollIntoView?.(scrollIntoViewOptions);
    }

    if (route.params?.scrollTo === 'PROMOTIONS_AND_PROMOTION_PERIOD') {
      promotionsAndPromotionPeriodRef.current?.scrollIntoView?.(scrollIntoViewOptions);
    }
  }, [route.params?.scrollTo]);

  return (
    <PromotionsTextProvider isSm={isSm} navigation={navigation} openLink={openLink}>
      {isFullSwing ? (
        <FSHeader
          title="PROMOTIONS TERMS AND CONDITIONS" goBack={onExit} hideLogo hideGameLogo hideAccount hideBalance hideMenu
          px={20}
        />
      ) : (
        <SupportGuideHeader title="PROMOTIONS TERMS AND CONDITIONS" onExit={onExit}/>
      )}

      <ScrollView keyboardShouldPersistTaps="handled"
                  contentContainerStyle={[MainStyle.min_h_full, {
                    backgroundColor: isFullSwing ? 'transparent' : colors.bg_contest, paddingBottom: isSm ? 27 : 92
                  }]}>
        <View style={[MainStyle.max_w_1120, MainStyle.full_w, {
          alignSelf: 'center', paddingTop: isSm ? 17 : 27, paddingHorizontal: isSm ? 40 : 0
        }]}>
          <IntroductionSection/>
          <PromotionPeriodsSection headingRef={setPromotionsAndPromotionPeriodRef}/>
          <PromotionCommunicationsSection/>
          <EligibilitySection headingRef={setEligibilitySectionRef}/>
          <ParticipationSection/>
          <ExcludedPlayersSection/>
          <QualifyingDepositsSection/>
          <PaymentBonusesSection/>
          <PrizesSection/>
          <PrizeDrawsSection/>
          <PublicitySection/>
          <PlayRestrictionsSection/>
          <CurrencySection/>
          <LiabilitySection/>
          <AlterationSection/>
          <GeneralSection/>
          <HoleInOneSection/>
        </View>
      </ScrollView>

      {!isSm && <SupportGuideButtonMenu onExit={onExit}/>}
    </PromotionsTextProvider>
  );
};

export default PromotionsScreen;
